import { initProStudioUrqlGql } from '@pypestream/api-services/urql';

import { sendUserMessage } from '../../app.xstate-utils';
import { getCustomerPypes } from '../../../utils';
import { ProStudioContext } from '../../prostudio.xstate.types';

export interface UpdateSurveyBotStreamsParams {
  bots: ProStudioContext['solutions'];
  eventBots: string[] | undefined;
  environment: string;
  uniqueCustomerId: string;
  projectId: string;
  botId: string;
  userInfo: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export const updateSurveyBotStreams = async ({
  bots,
  eventBots,
  environment,
  uniqueCustomerId,
  projectId,
  botId,
  userInfo,
}: UpdateSurveyBotStreamsParams): Promise<void> => {
  const urqlGql = initProStudioUrqlGql({
    userId: userInfo.id,
    userEmail: userInfo.email,
    userFirstName: userInfo.firstName,
    userLastName: userInfo.lastName,
  });

  const { live, sandbox } = await getCustomerPypes({
    uniqueCustomerId,
    projectId,
  });

  const envPypes = environment === 'live' ? live : sandbox;

  const selectedBots = (
    bots?.filter(
      (bot) =>
        bot.botType === 'main' && bot.id && (eventBots || []).includes(bot.id)
    ) || []
  ).map(({ botSolutionId: name, botType: type }) => ({
    name,
    type,
  }));

  const promises = envPypes
    ?.flatMap((pype) => {
      const { streams } = pype;

      const res =
        selectedBots?.flatMap(({ name: botName }) => {
          const result =
            streams?.map((stream) => {
              const startChatBotId = botName;

              const updateStreamBot =
                environment === 'live'
                  ? urqlGql.updateStreamBotLive
                  : urqlGql.updateStreamBotSandbox;

              if (stream?.['start_chat_bot_id'] === startChatBotId) {
                return updateStreamBot({
                  pypeId: pype.id || '',
                  streamId: stream.id || '',
                  customerId: uniqueCustomerId,
                  endChatBotId: botId,
                  startChatBotId: startChatBotId || '',
                  projectId,
                });
              }

              return null;
            }) || [];

          return result;
        }) || [];

      return res;
    })
    .filter(Boolean);

  await Promise.all(promises);
};

const FILE_UPLOAD_FAILED_MESSAGE =
  'File upload failed. Please upload the files manually from the Supervisor config panel.';

export const uploadSolutionFiles = async (
  solutionVersionId: string,
  files: File[]
): Promise<void> => {
  const uploadUrl = `${import.meta.env.FE_ADMIN_URL}/rest/solution-version-upload-files/${solutionVersionId}`;
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      sendUserMessage({
        type: 'error',
        text: FILE_UPLOAD_FAILED_MESSAGE,
        autoClose: 3000,
      });
      return;
    }
  } catch {
    sendUserMessage({
      type: 'error',
      text: FILE_UPLOAD_FAILED_MESSAGE,
      autoClose: 3000,
    });
  }
};
